"use client";

import { useCreditsContext } from "@/contexts/CreditsContext";
import { useRouter } from "next/navigation";
import { useEffect, useRef } from "react";
import { storePurchaseContext } from "@/libs/ga";
import { trackFunnel } from "@/libs/analytics";

interface InsufficientCreditsModalProps {
  isOpen: boolean;
  onClose: () => void;
  creditsRequired: number;
  toolName: string;
}

export default function InsufficientCreditsModal({
  isOpen,
  onClose,
  creditsRequired,
  toolName,
}: InsufficientCreditsModalProps) {
  const { credits } = useCreditsContext();
  const router = useRouter();
  const trackedRef = useRef(false);

  // Track when insufficient credits modal is shown (only once per opening)
  useEffect(() => {
    if (isOpen && !trackedRef.current) {
      trackFunnel('monetization', 'insufficient_credits_modal_shown', {
        tool_name: toolName,
        credits_required: creditsRequired,
        current_credits: credits,
        deficit: creditsRequired - (credits || 0)
      });
      trackedRef.current = true;
    }

    // Reset when modal closes
    if (!isOpen) {
      trackedRef.current = false;
    }
  }, [isOpen, toolName, creditsRequired, credits]);

  const handleGetMoreCredits = () => {
    // Store forced purchase trigger context
    storePurchaseContext({
      price_id: '', // Will be set when user selects package
      mode: 'payment',
      purchase_trigger: 'forced',
      source: 'insufficient_credits_modal',
      tool_name: toolName,
      credits_required: creditsRequired,
      current_credits: credits
    });

    // Track when user clicks to get more credits from insufficient credits modal
    trackFunnel('monetization', 'pricing_viewed', {
      source: 'insufficient_credits_modal',
      purchase_trigger: 'forced', // User was forced to see this due to insufficient credits
      tool_name: toolName,
      credits_required: creditsRequired,
      current_credits: credits
    });

    onClose();
    router.push("/dashboard/pricing");
  };

  const handleDismiss = (method: 'close_button' | 'maybe_later') => {
    // Track modal dismissal for funnel drop-off analysis
    trackFunnel('monetization', 'insufficient_credits_modal_dismissed', {
      tool_name: toolName,
      credits_required: creditsRequired,
      current_credits: credits,
      deficit: creditsRequired - (credits || 0),
      dismiss_method: method
    });

    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[99999] overflow-y-auto bg-black bg-opacity-50">
      <div className="min-h-screen md:px-4 md:py-8 flex items-center justify-center">
        <div className="bg-white rounded-none md:rounded-2xl shadow-2xl max-w-lg w-full min-h-screen md:min-h-0 transform transition-all duration-300 scale-100 relative">
          {/* Close button */}
          <button
            onClick={() => handleDismiss('close_button')}
            className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors z-10"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>

          <div className="p-8 text-center">
            {/* Credit Icon */}
            <div className="mx-auto flex items-center justify-center h-20 w-20 rounded-full bg-primary mb-6 shadow-lg">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="currentColor"
                className="h-10 w-10 text-white"
              >
                <path fillRule="evenodd" d="M10.788 3.21c.448-1.077 1.976-1.077 2.424 0l2.082 5.006 5.404.434c1.164.093 1.636 1.545.749 2.305l-4.117 3.527 1.257 5.273c.271 1.136-.964 2.033-1.96 1.425L12 18.354 7.373 21.18c-.996.608-2.231-.29-1.96-1.425l1.257-5.273-4.117-3.527c-.887-.76-.415-2.212.749-2.305l5.404-.434 2.082-5.005Z" clipRule="evenodd" />
              </svg>
            </div>

            {/* Title */}
            <h3 className="text-2xl font-bold text-gray-900 mb-4">
              Insufficient Credits
            </h3>

            {/* Message */}
            <div className="space-y-4 mb-6">
              <p className="text-gray-600">
                You need{" "}
                <span className="inline-flex items-center gap-1 font-semibold text-primary">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                    className="w-4 h-4"
                  >
                    <path fillRule="evenodd" d="M10.788 3.21c.448-1.077 1.976-1.077 2.424 0l2.082 5.006 5.404.434c1.164.093 1.636 1.545.749 2.305l-4.117 3.527 1.257 5.273c.271 1.136-.964 2.033-1.96 1.425L12 18.354 7.373 21.18c-.996.608-2.231-.29-1.96-1.425l1.257-5.273-4.117-3.527c-.887-.76-.415-2.212.749-2.305l5.404-.434 2.082-5.005Z" clipRule="evenodd" />
                  </svg>
                  {creditsRequired}
                </span>{" "}
                to use the{" "}
                <span className="font-semibold text-gray-900">{toolName}</span> tool.
              </p>

              <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-lg p-4 border border-gray-200">
                <p className="text-sm text-gray-500 mb-2">Your current balance</p>
                <div className="flex items-center justify-center gap-2">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                    className="w-6 h-6 text-primary"
                  >
                    <path fillRule="evenodd" d="M10.788 3.21c.448-1.077 1.976-1.077 2.424 0l2.082 5.006 5.404.434c1.164.093 1.636 1.545.749 2.305l-4.117 3.527 1.257 5.273c.271 1.136-.964 2.033-1.96 1.425L12 18.354 7.373 21.18c-.996.608-2.231-.29-1.96-1.425l1.257-5.273-4.117-3.527c-.887-.76-.415-2.212.749-2.305l5.404-.434 2.082-5.005Z" clipRule="evenodd" />
                  </svg>
                  <p className="text-3xl font-bold text-gray-900">{credits || 0}</p>
                </div>
              </div>

              <p className="text-gray-600">
                Get more credits to unlock the full power of PFPMaker and continue creating amazing images!
              </p>
            </div>

            {/* CTA Buttons */}
            <div className="space-y-3">
              <button
                onClick={handleGetMoreCredits}
                className="w-full bg-gradient-to-r from-primary to-primary/80 hover:from-primary hover:to-primary/100 text-white font-semibold py-4 px-6 rounded-lg transition-all duration-200 transform hover:scale-[1.02] shadow-lg hover:shadow-xl"
              >
                🚀 Get More Credits
              </button>

              <button
                onClick={() => handleDismiss('maybe_later')}
                className="w-full bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium py-3 px-6 rounded-lg transition-colors duration-200"
              >
                Maybe Later
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
